import type { ClockDayItem } from '../../api/showClockDays'
import type { ClockLiveProjection, ClockLiveProjectionItem } from '../../api/showClockLive'
import { resolveClockEpochMs } from '../../utils/showClock'
import { formatStationDateTime } from '../../utils/time'

let projectionCallCount = 0
let projectionLastLogMs = 0
const projectionMemo: {
  itemsRef: ClockDayItem[] | null
  previousProjectionRef: ClockLiveProjection | null | undefined
  nowEpochMs: number | null
  stationTimeZone: string | null | undefined
  baseStartEpochMs: number | null | undefined
  result: ClockLiveProjection | null
  signature: string | null
} = {
  itemsRef: null,
  previousProjectionRef: null,
  nowEpochMs: null,
  stationTimeZone: null,
  baseStartEpochMs: null,
  result: null,
  signature: null,
}

export const sortClockDayItems = (items: ClockDayItem[]): ClockDayItem[] => {
  return [...items].sort((a, b) => {
    const orderA = Number.isFinite(a.sort_order) ? Number(a.sort_order) : 0
    const orderB = Number.isFinite(b.sort_order) ? Number(b.sort_order) : 0
    if (orderA !== orderB) {
      return orderA - orderB
    }
    return a.id - b.id
  })
}

interface SequentialProjectionParams {
  items: ClockDayItem[]
  previousProjection?: ClockLiveProjection | null
  nowEpochMs: number
  stationTimeZone?: string | null
  baseStartEpochMs?: number | null
}

const isAnchorItem = (item: ClockDayItem): boolean => {
  if (!item) {
    return false
  }
  const fixed = typeof item.fixed_time === 'string' ? item.fixed_time : ''
  return fixed.trim().length > 0
}

const cloneProjection = (projection?: ClockLiveProjection | null): ClockLiveProjection | null => {
  if (!projection) {
    return null
  }
  return {
    ahead_behind_total: projection.ahead_behind_total,
    next_fixed_at: projection.next_fixed_at ?? null,
    seconds_to_next_fixed: projection.seconds_to_next_fixed ?? null,
    next_fixed_delta: projection.next_fixed_delta ?? null,
    items: projection.items ? projection.items.map((entry) => ({ ...entry })) : [],
  }
}

const toEpoch = (value: unknown, stationTimeZone?: string | null): number | null => {
  if (value === null || value === undefined) {
    return null
  }
  return resolveClockEpochMs(value as string, stationTimeZone)
}

const UNKNOWN_DURATION_FALLBACK_MS = 30 * 1000

const toDurationMs = (seconds: unknown): number => {
  if (!Number.isFinite(seconds)) {
    return UNKNOWN_DURATION_FALLBACK_MS
  }
  const numeric = Number(seconds)
  if (!Number.isFinite(numeric)) {
    return UNKNOWN_DURATION_FALLBACK_MS
  }
  if (numeric < 0) {
    return UNKNOWN_DURATION_FALLBACK_MS
  }
  return Math.round(numeric) * 1000
}

const safeDateTime = (
  epochMs: number | null,
  stationTimeZone: string | null | undefined,
  fallbackEpochMs: number,
): string => {
  if (!Number.isFinite(epochMs ?? Number.NaN) || epochMs === null) {
    return formatStationDateTime(Math.round(fallbackEpochMs), stationTimeZone)
  }
  return formatStationDateTime(Math.round(epochMs), stationTimeZone)
}

export const computeSequentialProjection = ({
  items,
  previousProjection,
  nowEpochMs,
  stationTimeZone,
  baseStartEpochMs,
}: SequentialProjectionParams): ClockLiveProjection => {
  const signatureParts = ['len', items.length.toString()]
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]
    signatureParts.push(
      String(item.id),
      item.actual_start ?? '',
      item.actual_end ?? '',
      item.skipped ? '1' : '0',
      String(item.planned_duration_sec ?? ''),
      item.fixed_time ?? '',
      item.item_type ?? '',
    )
  }
  signatureParts.push(
    String(nowEpochMs ?? ''),
    stationTimeZone ?? '',
    String(baseStartEpochMs ?? ''),
    previousProjection ? 'p1' : 'p0',
  )
  const signature = signatureParts.join('|')

  if (
    projectionMemo.result &&
    projectionMemo.signature === signature
  ) {
    return projectionMemo.result
  }

  const perfStart = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  const resolvedNowMs = Number.isFinite(nowEpochMs ?? Number.NaN)
    ? Math.max(0, Math.round(nowEpochMs))
    : Date.now()
  const baseStart = Number.isFinite(baseStartEpochMs ?? Number.NaN)
    ? Math.max(0, Math.round(baseStartEpochMs as number))
    : null
  const sortedItems = sortClockDayItems(items)
  const baseProjection = cloneProjection(previousProjection)
  const projectionMap = new Map<number, ClockLiveProjectionItem>()

  baseProjection?.items?.forEach((entry) => {
    if (entry && typeof entry.id === 'number') {
      projectionMap.set(entry.id, entry)
    }
  })

  const results: ClockLiveProjectionItem[] = []
  let aheadBehindTotalSeconds = 0
  let cursorMs = baseStart ?? resolvedNowMs
  let scheduledCursorMs: number | null = baseStart
  let nextFixedAt: string | null = null
  let secondsToNextFixed: number | null = null
  let nextFixedDelta: number | null = null
  let anchorResolved = false

  sortedItems.forEach((item) => {
    const durationMs = toDurationMs(item.planned_duration_sec)
    const previousEntry = projectionMap.get(item.id) ?? null
    const previousStartMs = previousEntry ? toEpoch(previousEntry.projected_start, stationTimeZone) : null
    const previousEndMs = previousEntry ? toEpoch(previousEntry.projected_end, stationTimeZone) : null
    const previousDeltaSeconds = previousEntry ? Number(previousEntry.delta_from_schedule) : null
    const fixedTimeMs = toEpoch(item.fixed_time, stationTimeZone)

    let scheduledStartMs: number | null = null
    let scheduledEndMs: number | null = null
    let baselineScheduledStartMs: number | null = null

    if (previousStartMs !== null && Number.isFinite(previousDeltaSeconds ?? Number.NaN)) {
      scheduledStartMs = previousStartMs - Math.round(Number(previousDeltaSeconds)) * 1000
    }

    if (isAnchorItem(item) && fixedTimeMs !== null) {
      scheduledStartMs = fixedTimeMs
    } else if (scheduledStartMs === null && scheduledCursorMs !== null) {
      scheduledStartMs = scheduledCursorMs
    }

    if (scheduledStartMs === null) {
      scheduledStartMs = resolvedNowMs
    }

    baselineScheduledStartMs = scheduledStartMs
    scheduledEndMs = scheduledStartMs + durationMs
    scheduledCursorMs = scheduledEndMs

    const actualStartMs = toEpoch(item.actual_start, stationTimeZone)
    const actualEndMs = toEpoch(item.actual_end, stationTimeZone)

    let projectedStartMs: number | null = null
    let projectedEndMs: number | null = null

    if (item.skipped) {
      projectedStartMs = resolvedNowMs
      projectedEndMs = resolvedNowMs
    } else if (actualEndMs !== null) {
      projectedEndMs = actualEndMs
      if (actualStartMs !== null) {
        projectedStartMs = actualStartMs
      } else if (durationMs > 0) {
        projectedStartMs = actualEndMs - durationMs
      } else {
        projectedStartMs = actualEndMs
      }
    } else if (actualStartMs !== null) {
      projectedStartMs = actualStartMs
      projectedEndMs = actualStartMs + durationMs
      if (projectedEndMs < projectedStartMs) {
        projectedEndMs = projectedStartMs
      }
    } else if (isAnchorItem(item) && fixedTimeMs !== null) {
      // For display, keep the organic projected start (cursor), but enforce the anchor for downstream timing.
      projectedStartMs = cursorMs
      projectedEndMs = cursorMs + durationMs
      // Reset cursors at the anchor so later items don't inherit drift
      const anchorEnd = fixedTimeMs + durationMs
      cursorMs = anchorEnd
      scheduledCursorMs = anchorEnd
    } else {
      const candidate = cursorMs > resolvedNowMs ? cursorMs : resolvedNowMs
      projectedStartMs = candidate
      projectedEndMs = candidate + durationMs
    }

    if (projectedStartMs === null) {
      projectedStartMs = previousStartMs ?? scheduledStartMs ?? resolvedNowMs
    }

    if (projectedEndMs === null) {
      if (projectedStartMs !== null && durationMs > 0) {
        projectedEndMs = projectedStartMs + durationMs
      } else {
        projectedEndMs = previousEndMs ?? projectedStartMs ?? resolvedNowMs
      }
    }

    if (projectedStartMs === null) {
      projectedStartMs = resolvedNowMs
    }

    if (projectedEndMs === null) {
      projectedEndMs = projectedStartMs
    }

    const isFixedAnchor = isAnchorItem(item) && fixedTimeMs !== null
    if (!isFixedAnchor) {
      cursorMs = Math.max(cursorMs, projectedEndMs)
    }

    let deltaSeconds = 0
    const deltaBaseline = isAnchorItem(item) && fixedTimeMs !== null ? fixedTimeMs : scheduledStartMs
    if (deltaBaseline !== null && projectedStartMs !== null) {
      deltaSeconds = Math.round((projectedStartMs - deltaBaseline) / 1000)
    } else if (Number.isFinite(previousDeltaSeconds ?? Number.NaN)) {
      deltaSeconds = Math.round(Number(previousDeltaSeconds))
    }

    if (scheduledEndMs !== null && projectedEndMs !== null) {
      aheadBehindTotalSeconds = Math.round((projectedEndMs - scheduledEndMs) / 1000)
    }

    const anchorCandidate = isAnchorItem(item)
    if (!anchorResolved && anchorCandidate && !item.skipped && actualEndMs === null && actualStartMs === null) {
      if (fixedTimeMs !== null && projectedStartMs !== null) {
        anchorResolved = true
        nextFixedAt = formatStationDateTime(Math.round(fixedTimeMs), stationTimeZone)
        secondsToNextFixed = Math.round((fixedTimeMs - resolvedNowMs) / 1000)
        nextFixedDelta = Math.round((projectedStartMs - fixedTimeMs) / 1000)
      }
    }

    results.push({
      id: item.id,
      projected_start: safeDateTime(projectedStartMs, stationTimeZone, resolvedNowMs),
      projected_end: safeDateTime(projectedEndMs, stationTimeZone, resolvedNowMs),
      is_fixed: anchorCandidate,
      delta_from_schedule: deltaSeconds,
      scheduled_start: safeDateTime(anchorCandidate && fixedTimeMs !== null ? fixedTimeMs : scheduledStartMs, stationTimeZone, resolvedNowMs),
      scheduled_end: safeDateTime(anchorCandidate && fixedTimeMs !== null ? fixedTimeMs + durationMs : scheduledEndMs, stationTimeZone, resolvedNowMs),
    })
  })

  const projection = {
    items: results,
    ahead_behind_total: aheadBehindTotalSeconds,
    next_fixed_at: nextFixedAt,
    seconds_to_next_fixed: secondsToNextFixed,
    next_fixed_delta: nextFixedDelta,
  }

  projectionMemo.itemsRef = items
  projectionMemo.previousProjectionRef = previousProjection
  projectionMemo.nowEpochMs = nowEpochMs
  projectionMemo.stationTimeZone = stationTimeZone
  projectionMemo.baseStartEpochMs = baseStartEpochMs
  projectionMemo.result = projection
  projectionMemo.signature = signature

  const perfEnd = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  const durationMs = perfEnd - perfStart
  projectionCallCount += 1
  const nowMs = perfEnd
  if (durationMs > 50 && nowMs - projectionLastLogMs > 2000) {
    // Lightweight debug signal to help spot runaway projection loops in the field.
    console.warn('computeSequentialProjection slow', {
      durationMs: Math.round(durationMs),
      itemCount: items.length,
      baseStartEpochMs,
      nowEpochMs,
    })
  }
  if (nowMs - projectionLastLogMs > 5000) {
    console.info('computeSequentialProjection rate', {
      calls: projectionCallCount,
      lastDurationMs: Math.round(durationMs),
      itemCount: items.length,
    })
    projectionCallCount = 0
    projectionLastLogMs = nowMs
  }

  return projection
}
